/*
  ErrorBoundary — catches render-phase throws inside any subtree so a
  single bad component can't nuke the entire app to a white screen.

  Renders a compact fallback card that:
    - Shows the error message + a truncated component stack.
    - Offers "Copy details" so a user can paste the full stack to
      support in one tap (production stacks are minified, but the
      message + component chain is usually enough to narrow it down).
    - Offers "Try again" (resets local state and re-renders the
      children) and "Reload page" as escape hatches.

  Wrap around each top-level route so a crash in Client Detail
  doesn't take down the sidebar / header / other tabs.
*/
import { Component } from "react";
import { AlertTriangle, Copy, RotateCcw, RefreshCw } from "lucide-react";

export default class ErrorBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = { error: null, info: null };
  }

  static getDerivedStateFromError(error) {
    return { error };
  }

  componentDidCatch(error, info) {
    this.setState({ info });
    // Always log — prod builds strip stacks but keep the message.
    // eslint-disable-next-line no-console
    console.error("[ErrorBoundary]", error, info?.componentStack);
  }

  handleReset = () => {
    this.setState({ error: null, info: null });
  };

  handleReload = () => {
    window.location.reload();
  };

  handleCopy = async () => {
    const { error, info } = this.state;
    const text = [
      `URL: ${window.location.href}`,
      `Error: ${error?.name || "Error"}: ${error?.message || String(error)}`,
      "",
      "Stack:",
      error?.stack || "(no stack)",
      "",
      "Component stack:",
      info?.componentStack || "(no component stack)",
    ].join("\n");
    try {
      await navigator.clipboard.writeText(text);
    } catch {
      /* clipboard blocked — user can still read the panel */
    }
  };

  render() {
    const { error, info } = this.state;
    if (!error) return this.props.children;

    return (
      <div
        className="max-w-2xl mx-auto my-10 rounded-lg border border-red-300 bg-red-50 p-5 text-red-950"
        role="alert"
        data-testid="error-boundary-fallback"
      >
        <div className="flex items-center gap-2 text-red-900 font-semibold text-sm mb-2">
          <AlertTriangle className="h-4 w-4" />
          Something went wrong on this screen.
        </div>
        <p className="text-sm mb-3">
          The page hit an unexpected error and stopped rendering. You can
          retry — the rest of the app is still working.
        </p>
        <div className="rounded border border-red-200 bg-white p-3 mb-3 font-mono text-2xs text-red-900 max-h-48 overflow-auto">
          <div className="font-semibold mb-1">
            {error?.name || "Error"}: {error?.message || String(error)}
          </div>
          {info?.componentStack && (
            <pre className="whitespace-pre-wrap">{info.componentStack}</pre>
          )}
        </div>
        <div className="flex gap-2 flex-wrap">
          <button
            type="button"
            onClick={this.handleReset}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs rounded border border-red-300 bg-white hover:bg-red-100"
            data-testid="error-boundary-retry"
          >
            <RotateCcw className="h-3.5 w-3.5" /> Try again
          </button>
          <button
            type="button"
            onClick={this.handleReload}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs rounded border border-red-300 bg-white hover:bg-red-100"
            data-testid="error-boundary-reload"
          >
            <RefreshCw className="h-3.5 w-3.5" /> Reload page
          </button>
          <button
            type="button"
            onClick={this.handleCopy}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs rounded border border-red-300 bg-white hover:bg-red-100"
            data-testid="error-boundary-copy"
          >
            <Copy className="h-3.5 w-3.5" /> Copy details
          </button>
        </div>
      </div>
    );
  }
}
